import { MasterClientProductViewDto } from '../models/api-data-contracts';

/** Local calendar date as YYYY-MM-DD (no UTC conversion). */
export function toCalendarDateString(value: Date | string | null | undefined): string | null {
  const date = parseLandingDate(value);
  if (!date) return null;
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Calendar date from an API ISO value, ignoring timezone so 2027-01-01 stays January 1. */
export function parseLandingDate(iso: string | Date | null | undefined): Date | null {
  if (!iso) return null;
  if (iso instanceof Date) {
    if (Number.isNaN(iso.getTime())) return null;
    return new Date(iso.getFullYear(), iso.getMonth(), iso.getDate());
  }
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function isProductActiveOn(
  product: MasterClientProductViewDto,
  effectiveDate: Date | string,
): boolean {
  const start = parseLandingDate(product.startDate);
  const end = parseLandingDate(product.endDate);
  const asOf = parseLandingDate(effectiveDate);
  if (!start || !asOf) return false;
  return start <= asOf && (!end || end >= asOf);
}

/**
 * Products whose date range covers effectiveDate.
 * When several rows share the same productType + productName (price versions),
 * keeps the one with the latest startDate. Distinct plans (e.g. IDP Individual vs Family) stay.
 */
export function filterProductsActiveOn(
  products: MasterClientProductViewDto[],
  effectiveDate: Date | string,
): MasterClientProductViewDto[] {
  const active = products.filter(p => isProductActiveOn(p, effectiveDate));
  const bestByPlan = new Map<string, MasterClientProductViewDto>();
  for (const product of active) {
    const key = `${product.productType ?? ''}\0${product.productName ?? ''}`;
    const current = bestByPlan.get(key);
    if (!current || (parseLandingDate(product.startDate)?.getTime() ?? 0) > (parseLandingDate(current.startDate)?.getTime() ?? 0)) {
      bestByPlan.set(key, product);
    }
  }
  return [...bestByPlan.values()];
}

/**
 * Maps a user selection (any version of a plan) onto the pricing rows active on effectiveDate.
 * NH and OE can resolve the same Legal/IDP concept to different clientProductIds and rates.
 */
export function remapSelectionForDate(
  catalog: MasterClientProductViewDto[],
  selected: MasterClientProductViewDto[],
  effectiveDate: Date | string,
): MasterClientProductViewDto[] {
  const active = filterProductsActiveOn(catalog, effectiveDate);
  return selected
    .map(s =>
      active.find(p => p.productType === s.productType && p.productName === s.productName)
      ?? active.find(p => p.productType === s.productType)
    )
    .filter((p): p is MasterClientProductViewDto => !!p);
}

/** Same contact email as OTP/session: personal (secondary) when present, otherwise work/primary. */
export function enrollmentContactEmail(
  draft: { primaryEmail?: string | null; secondaryEmail?: string | null } | null | undefined
): string {
  const personal = draft?.secondaryEmail?.trim();
  if (personal) return personal;
  return draft?.primaryEmail?.trim() || '';
}

export function paymentFrequencyName(months: number){
    switch(months){
        case 1:
            return 'Monthly';
        case 3:
            return 'Quarterly';
        case 6:
            return 'Semi-annual';
        case 12:
            return 'Annual'; 
    }

    throw new Error('Invalid frequency');
}

export function calculatePlanRate(plans: { memberContribution?: number }[], frequency: number): number {
    const totalContribution = plans?.reduce((sum, p) => sum + (p?.memberContribution ?? 0), 0);
    return totalContribution * frequency;
}

export function calculatePlanRateWithDiscount(plans: { memberContribution?: number }[], frequency: number, discount: number): number {
    const total = calculatePlanRate(plans, frequency);
    return total * (1 - discount / 100);
}

export function calculateTotalPrice(selectedProducts: { [id: string]: { memberContribution?: number } }, frequency: number): number {
    if (!selectedProducts["legal"] && !selectedProducts["idp"]) return 0;

    const legalPrice = calculatePlanRate([selectedProducts["legal"]], frequency);
    const idpPrice = selectedProducts["idp"] 
        ? calculatePlanRate([selectedProducts["idp"]], frequency) 
        : 0;

    return legalPrice + idpPrice;
}

export function calculateTotalPriceWithDiscount(selectedProducts: { [id: string]: { memberContribution?: number } }, frequency: number, discount: number): number {
    if (!selectedProducts["legal"] && !selectedProducts["idp"]) return 0;
    const total = calculateTotalPrice(selectedProducts, frequency);
    return total * (1 - discount / 100);
}
